import { useState } from "react";
import Check from "@mui/icons-material/Check";
import Clear from "@mui/icons-material/Clear";
import MyLocation from "@mui/icons-material/MyLocation";
import { Box, Button, TextField, Tooltip, Typography } from "@mui/material";
import AddressPickerDialog from "features/maps/AddressPickerDialog";
import {
  useCreateCompanyOfficeMutation,
  useDeleteCompanyOfficeMutation,
  useUpdateCompanyOfficeMutation,
} from "app/api/companies";
import { Company } from "models/Company";
import { CompanyOffice } from "models/CompanyOffice";
import EmptyMessage from "components/EmptyMessage";
import Delete from "@mui/icons-material/Delete";

type OfficeFormProps = { company: Company; officeToEdit?: CompanyOffice; onClose: () => void };

const OfficeForm: React.FC<OfficeFormProps> = (props) => {
  const { company, officeToEdit, onClose } = props;

  const [showAddressPickerDialog, setShowAddressPickerDialog] = useState(false);

  const [address, setAddress] = useState(officeToEdit?.address || "");
  const [postalCode, setPostalCode] = useState(officeToEdit?.postalCode || "");
  const [city, setCity] = useState(officeToEdit?.city || "");
  const [latitude, setLatitude] = useState<number | null>(officeToEdit?.latitude || null);
  const [longitude, setLongitude] = useState<number | null>(officeToEdit?.longitude || null);
  const [hourlyRate, setHourlyRate] = useState<number | null>(officeToEdit?.hourlyRate || null);

  const [createOffice, { isLoading: isCreatingOffice }] = useCreateCompanyOfficeMutation();
  const [updateOffice, { isLoading: isUpdatingOffice }] = useUpdateCompanyOfficeMutation();
  const [deleteOffice, { isLoading: isDeletingOffice }] = useDeleteCompanyOfficeMutation();

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();

        if (!latitude || !longitude) return;

        (officeToEdit
          ? updateOffice({
              id: officeToEdit.id,
              companyId: company.id,
              address,
              postalCode,
              city,
              latitude,
              longitude,
              hourlyRate,
            })
          : createOffice({
              companyId: company.id,
              address,
              postalCode,
              city,
              latitude,
              longitude,
              hourlyRate,
            })
        )
          .unwrap()
          .then(() => onClose());
      }}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        width: "100%",
        maxWidth: 400,
      }}
    >
      {address && postalCode && city ? (
        <Box sx={{ width: "100%" }}>
          <Typography fontWeight="bold">{address}</Typography>
          <Typography color="text.secondary">
            {postalCode} {city}
          </Typography>
        </Box>
      ) : (
        <EmptyMessage>Ingen adress vald</EmptyMessage>
      )}

      <Button
        variant="outlined"
        fullWidth
        onClick={() => setShowAddressPickerDialog(true)}
        startIcon={<MyLocation />}
      >
        Välj på karta
      </Button>

      {showAddressPickerDialog && (
        <AddressPickerDialog
          initialAddress={{
            address: address,
            postalCode: postalCode,
            city: city,
            latitude: latitude || 0,
            longitude: longitude || 0,
          }}
          onSelectAddress={(address) => {
            setAddress(address.address);
            setPostalCode(address.postalCode);
            setCity(address.city);
            setLatitude(address.latitude || 0);
            setLongitude(address.longitude || 0);
            setShowAddressPickerDialog(false);
          }}
          onClose={() => setShowAddressPickerDialog(false)}
        />
      )}

      <TextField
        name="hourlyRate"
        type="number"
        label="Timpris (om avvikande)"
        value={hourlyRate}
        onChange={(e) => setHourlyRate(Number(e.target.value))}
        variant="filled"
        fullWidth
        autoComplete="new-password"
      />

      <Tooltip title="Ta bort kontor" arrow>
        <Button
          variant="outlined"
          color="error"
          startIcon={<Delete />}
          hidden={!officeToEdit}
          loading={isDeletingOffice}
          sx={{ minWidth: 0, px: 1, ".MuiButton-startIcon": { mx: 0 } }}
          onClick={() => {
            if (!officeToEdit) return;

            deleteOffice({ id: officeToEdit.id, companyId: company.id })
              .unwrap()
              .then(() => onClose());
          }}
        />
      </Tooltip>

      <Button variant="outlined" startIcon={<Clear />} sx={{ ml: "auto" }} onClick={onClose}>
        Avbryt
      </Button>

      <Button
        type="submit"
        variant="contained"
        startIcon={<Check />}
        disabled={!address || !postalCode || !city || !latitude || !longitude}
        loading={isCreatingOffice || isUpdatingOffice}
      >
        Spara
      </Button>
    </Box>
  );
};

export default OfficeForm;
